import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import './Roles.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const [newRole, setNewRole] = useState({
    name: '',
    display_name: '',
    description: '',
    color: '#3b82f6',
    priority: 0,
    show_in_team: false,
    display_order: 0
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/roles`, {
        withCredentials: true
      });
      setRoles(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles');
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/roles/permissions/list`, {
        withCredentials: true
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/roles`, newRole, {
        withCredentials: true
      });
      toast.success('Role created successfully');
      setShowCreateModal(false);
      setNewRole({
        name: '',
        display_name: '',
        description: '',
        color: '#3b82f6',
        priority: 0,
        show_in_team: false,
        display_order: 0
      });
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create role');
    }
  };

  const handleEditRole = async (e) => {
    e.preventDefault();
    try {
      // Update basic role info
      await axios.put(`${API_URL}/api/roles/${selectedRole.id}`, selectedRole, {
        withCredentials: true
      });
      
      // Update permissions
      await axios.put(
        `${API_URL}/api/roles/${selectedRole.id}/permissions`,
        { permission_ids: selectedRole.selectedPermissions },
        { withCredentials: true }
      );
      
      toast.success('Role updated successfully');
      setShowEditModal(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/roles/${roleId}`, {
        withCredentials: true
      });
      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete role');
    }
  };

  const handleOpenEdit = async (role) => {
    try {
      const response = await axios.get(`${API_URL}/api/roles/${role.id}`, {
        withCredentials: true
      });
      setSelectedRole({
        ...response.data,
        selectedPermissions: response.data.permissions.map(p => p.id)
      });
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to load role details');
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedRole(prev => {
      const selectedPermissions = prev.selectedPermissions || [];
      const isSelected = selectedPermissions.includes(permissionId);
      
      return {
        ...prev,
        selectedPermissions: isSelected
          ? selectedPermissions.filter(id => id !== permissionId)
          : [...selectedPermissions, permissionId]
      };
    });
  };

  const handleSavePermissions = async () => {
    try {
      // Update basic role info
      await axios.put(`${API_URL}/api/roles/${selectedRole.id}`, selectedRole, {
        withCredentials: true
      });
      
      // Update permissions
      await axios.put(
        `${API_URL}/api/roles/${selectedRole.id}/permissions`,
        { permission_ids: selectedRole.selectedPermissions },
        { withCredentials: true }
      );
      
      toast.success('Role and permissions updated successfully');
      setShowEditModal(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update role');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      panel: 'Panel Access',
      scripts: 'Scripts',
      support: 'Support',
      admin: 'Administration',
      general: 'General'
    };
    return labels[category] || category;
  };

  if (loading) {
    return <div className="admin-loading">Loading roles...</div>;
  }

  return (
    <div className="roles-management">
      <div className="roles-header">
        <div>
          <h1>Roles & Permissions</h1>
          <p>Manage user roles and their permissions</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          ➕ Create Role
        </button>
      </div>

      <div className="roles-grid">
        {roles.map(role => (
          <div key={role.id} className="role-card glass">
            <div className="role-header">
              <div className="role-info">
                <h3 style={{ color: role.color }}>{role.display_name}</h3>
                <span className="role-name">{role.name}</span>
              </div>
              <div className="role-badges">
                {role.is_default && <span className="badge badge-blue">Default</span>}
                {role.is_verified_role && <span className="badge badge-green">Verified</span>}              {role.show_in_team && <span className="badge badge-purple">Team</span>}              </div>
            </div>

            <p className="role-description">{role.description}</p>

            <div className="role-stats">
              <div className="stat">
                <span className="stat-icon">👥</span>
                <span className="stat-value">{role.user_count}</span>
                <span className="stat-label">Users</span>
              </div>
              <div className="stat">
                <span className="stat-icon">🔒</span>
                <span className="stat-value">{role.permission_count}</span>
                <span className="stat-label">Permissions</span>
              </div>
              <div className="stat">
                <span className="stat-icon">⭐</span>
                <span className="stat-value">{role.priority}</span>
                <span className="stat-label">Priority</span>
              </div>
            </div>

            <div className="role-actions">
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => handleOpenEdit(role)}
              >
                ✏️ Edit
              </button>
              <button 
                className="btn btn-danger btn-sm"
                onClick={() => handleDeleteRole(role.id)}
                disabled={role.user_count > 0}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Role</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateRole}>
              <div className="form-group">
                <label>Role Name (Internal)</label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={e => setNewRole({...newRole, name: e.target.value})}
                  placeholder="e.g., moderator"
                  required
                />
              </div>
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={newRole.display_name}
                  onChange={e => setNewRole({...newRole, display_name: e.target.value})}
                  placeholder="e.g., Moderator"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newRole.description}
                  onChange={e => setNewRole({...newRole, description: e.target.value})}
                  placeholder="Role description"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="color"
                    value={newRole.color}
                    onChange={e => setNewRole({...newRole, color: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <input
                    type="number"
                    value={newRole.priority}
                    onChange={e => setNewRole({...newRole, priority: parseInt(e.target.value)})}
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newRole.show_in_team || false}
                      onChange={e => setNewRole({...newRole, show_in_team: e.target.checked})}
                    />
                    Show in Team section on homepage
                  </label>
                </div>
                <div className="form-group">
                  <label>Team Display Order</label>
                  <input
                    type="number"
                    value={newRole.display_order}
                    onChange={e => setNewRole({...newRole, display_order: parseInt(e.target.value)})}
                    min="0"
                    placeholder="Display order in team section"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && selectedRole && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Role: {selectedRole.display_name}</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <div className="modal-tabs">
              <button className="tab-btn active">General</button>
              <button className="tab-btn">Permissions</button>
            </div>

            <form onSubmit={handleEditRole}>
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={selectedRole.display_name}
                  onChange={e => setSelectedRole({...selectedRole, display_name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={selectedRole.description || ''}
                  onChange={e => setSelectedRole({...selectedRole, description: e.target.value})}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="color"
                    value={selectedRole.color}
                    onChange={e => setSelectedRole({...selectedRole, color: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <input
                    type="number"
                    value={selectedRole.priority}
                    onChange={e => setSelectedRole({...selectedRole, priority: parseInt(e.target.value)})}
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedRole.is_default || false}
                      onChange={e => setSelectedRole({...selectedRole, is_default: e.target.checked})}
                    />
                    Default Role (assigned to new users)
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedRole.is_verified_role || false}
                      onChange={e => setSelectedRole({...selectedRole, is_verified_role: e.target.checked})}
                    />
                    Verified Role (assigned after script verification)
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedRole.show_in_team || false}
                      onChange={e => setSelectedRole({...selectedRole, show_in_team: e.target.checked})}
                    />
                    Show in Team section on homepage
                  </label>
                </div>
                <div className="form-group">
                  <label>Team Display Order</label>
                  <input
                    type="number"
                    value={selectedRole.display_order || 0}
                    onChange={e => setSelectedRole({...selectedRole, display_order: parseInt(e.target.value)})}
                    min="0"
                    placeholder="Display order in team section"
                  />
                </div>
              </div>

              <div className="permissions-section">
                <h3>Permissions</h3>
                <div className="permissions-grid">
                  {Object.entries(permissions).map(([category, perms]) => (
                    <div key={category} className="permission-category">
                      <h4>{getCategoryLabel(category)}</h4>
                      <div className="permission-list">
                        {perms.map(perm => (
                          <label key={perm.id} className="permission-item">
                            <input
                              type="checkbox"
                              checked={selectedRole.selectedPermissions?.includes(perm.id) || false}
                              onChange={() => handlePermissionToggle(perm.id)}
                            />
                            <div className="permission-info">
                              <span className="permission-name">{perm.display_name}</span>
                              <span className="permission-desc">{perm.description}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" onClick={handleSavePermissions}>
                  Save All Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Roles;
